'use client';

import { Suspense } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';

import { useSearchParamsQuery } from '../hooks';

import type { ComponentType } from 'react';

/**
 * Configuration for creating a Suspense wrapper component.
 * This utility eliminates boilerplate for components that follow the pattern:
 * Suspense wrapper -> Container with useSuspenseQuery -> Component rendering
 */
interface CreateSuspenseWrapperConfig<TData, TComponentProps> {
  /** Cache tag(s) for React Query - can be a single tag or array of tags */
  cacheTag: string | string[];

  /** Fetcher function that accepts query params and returns data */
  fetcher: (params: Record<string, unknown> | undefined) => Promise<TData>;

  /** Component to render with the fetched data */
  Component: ComponentType<TComponentProps>;

  /** Loading skeleton component */
  Skeleton: ComponentType;

  /** Whether to use search params from URL (defaults to true) */
  useSearchParams?: boolean;

  /** Options for useSearchParamsQuery hook */
  searchParamsOptions?: {
    sortEntries?: boolean;
  };

  /** Transform fetched data before passing to Component */
  transformData?: (data: TData) => TComponentProps;

  /** Build custom query key (receives cacheTag, searchKey, and hook results) */
  buildQueryKey?: (cacheTag: string | string[], searchKey: string, hookResults?: Record<string, unknown>) => unknown[];

  /** Additional hooks to call in the Container (e.g., useSession) */
  useAdditionalHooks?: () => Record<string, unknown>;

  /** Additional static parameters to pass to the server action */
  staticParams?: Record<string, unknown>;

  /** Build query parameters dynamically using hook results and search params */
  buildQueryParams?: (
    searchParamsObject: Record<string, unknown> | undefined,
    hookResults?: Record<string, unknown>
  ) => Record<string, unknown>;
}

/**
 * Creates a Suspense wrapper component with integrated data fetching.
 *
 * This factory function eliminates the boilerplate of creating separate Wrapper and Container
 * components for data fetching with Suspense and useSuspenseQuery.
 *
 * @example
 * export default createSuspenseWrapper({
 *   cacheTag: 'profile',
 *   fetcher: (params) => callServerAction('profile.getProfile', [params]),
 *   Component: ProfileContent,
 *   Skeleton: ProfileSkeleton,
 * });
 */
export function createSuspenseWrapper<TData = unknown, TComponentProps = unknown>(
  config: CreateSuspenseWrapperConfig<TData, TComponentProps>
) {
  const {
    cacheTag,
    fetcher,
    Component,
    Skeleton,
    useSearchParams = true,
    searchParamsOptions,
    transformData,
    buildQueryKey,
    useAdditionalHooks,
    staticParams,
    buildQueryParams,
  } = config;

  function Container() {
    // Call additional hooks if provided (e.g., useSession)
    const hookResults = useAdditionalHooks?.();

    // Always call useSearchParamsQuery to follow Rules of Hooks
    const searchParamsQuery = useSearchParamsQuery(searchParamsOptions);

    // Conditionally use search params based on config
    const searchParamsObject = useSearchParams
      ? (searchParamsQuery.searchParamsObject as Record<string, unknown> | undefined)
      : undefined;
    const searchKey = useSearchParams ? searchParamsQuery.searchKey : '';

    // Build query parameters
    const queryParams = buildQueryParams
      ? buildQueryParams(searchParamsObject, hookResults)
      : staticParams
        ? { ...staticParams, ...searchParamsObject }
        : searchParamsObject;

    // Build query key
    const queryKey = buildQueryKey ? buildQueryKey(cacheTag, searchKey, hookResults) : [cacheTag, searchKey];

    // Fetch data with useSuspenseQuery
    const { data } = useSuspenseQuery({
      queryKey,
      queryFn: () => fetcher(queryParams),
    });

    // Transform data if needed
    const componentProps = transformData ? transformData(data) : (data as unknown as TComponentProps);

    return <Component {...componentProps} key={JSON.stringify(data)} />;
  }

  function Wrapper() {
    return (
      <Suspense fallback={<Skeleton />}>
        <Container />
      </Suspense>
    );
  }

  // Set display names for debugging
  Wrapper.displayName = `SuspenseWrapper(${Component.displayName || Component.name || 'Component'})`;
  Container.displayName = `SuspenseContainer(${Component.displayName || Component.name || 'Component'})`;

  return Wrapper;
}

/**
 * Creates a Suspense wrapper for components that don't use search params.
 * Simplified version of createSuspenseWrapper for static parameter scenarios.
 */
export function createStaticSuspenseWrapper<TData = unknown, TComponentProps = unknown>(config: {
  cacheTag: string | string[];
  fetcher: (params: Record<string, unknown> | undefined) => Promise<TData>;
  Component: ComponentType<TComponentProps>;
  Skeleton: ComponentType;
  staticParams?: Record<string, unknown>;
  transformData?: (data: TData) => TComponentProps;
  buildQueryKey?: (cacheTag: string | string[]) => unknown[];
}) {
  return createSuspenseWrapper({
    ...config,
    useSearchParams: false,
  });
}
